import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { setCanonical, setMeta } from '@/utils/seo';
import { FAQSchema, BreadcrumbSchema, SpeakableSchema, ServiceSchema } from '@/components/SEOSchemas';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TEMPLATES = {
  'estate-planning': {
    titleFn: (name) => `Estate Planning Services in ${name}`,
    metaFn: (name, prov) => `Find estate planning services in ${name}, ${prov}. Online SPA, guardian mandates, and digital vault.`,
    heroFn: (name) => `Your family in ${name} deserves a clear plan — not confusion during a crisis. Let LegaSecure help you organize your estate, protect your children, and secure your assets.`,
    sections: (name) => [
      { title: `Why Estate Planning Matters in ${name}`, content: `Every family in ${name} deserves a clear plan for their assets and loved ones. Without proper estate planning, your family may face legal disputes, frozen bank accounts, and years of court proceedings.` },
      { title: 'Key Services Available', items: ['Online SPA (Special Power of Attorney)', 'Guardian Mandate for Children', 'Digital Vault with Zero-Knowledge Encryption', 'Inheritance Planning Tools', 'Property Management Authorization'] },
    ],
    faqsFn: (name) => [
      { question: `What estate planning services are available in ${name}?`, answer: `LegaSecure offers digital estate planning tools accessible from ${name}, including Online SPA, Guardian Mandates, and an encrypted Digital Vault.` },
      { question: `Do I need a lawyer for estate planning in ${name}?`, answer: `While consulting a lawyer is recommended for complex matters, LegaSecure provides tools to organize your estate plan. Visit legasecure.com to get started.` },
      { question: `How much does estate planning cost?`, answer: `LegaSecure offers a Free plan (5 documents), Standard (P249/mo), and Premium (P499/mo). Traditional lawyer-based planning can cost significantly more.` },
    ],
  },
  'property': {
    titleFn: (name) => `Property Management Services in ${name}`,
    metaFn: (name, prov) => `Manage your property in ${name}, ${prov} from anywhere. Online SPA for property transactions, title transfer guidance.`,
    heroFn: (name) => `Working abroad but own property in ${name}? You don't need to fly home. LegaSecure lets you manage, sell, or transfer your property remotely with an Online SPA.`,
    sections: (name) => [
      { title: `Property Challenges in ${name}`, content: `Managing property in ${name} from abroad requires proper legal authorization. Without an SPA, you cannot sell, mortgage, or even manage your property remotely.` },
      { title: 'What You Can Do Remotely', items: ['Sell property via Online SPA', 'Transfer land titles', 'Manage rental properties', 'Pay property taxes', 'Handle mortgage applications'] },
    ],
    faqsFn: (name) => [
      { question: `How do I sell property in ${name} while abroad?`, answer: `You need a Special Power of Attorney (SPA) authorizing your representative. LegaSecure lets you create an Apostille-ready SPA online.` },
      { question: `How do I transfer a land title in ${name}?`, answer: `Title transfer requires a Deed of Sale, tax payments at BIR, registration at Registry of Deeds. Your SPA-authorized representative handles this.` },
      { question: `Can I manage rental property remotely?`, answer: `Yes, with an SPA you can authorize someone to manage leases, collect rent, and handle maintenance in ${name}.` },
    ],
  },
  'notary': {
    titleFn: (name) => `Notarial & SPA Services in ${name}`,
    metaFn: (name, prov) => `Find notarial services and SPA assistance in ${name}, ${prov}. Notarize documents, create power of attorney.`,
    heroFn: (name) => `Skip the hassle of traditional notarization in ${name}. LegaSecure's Online SPA is Apostille-ready — create and sign documents from anywhere in the world.`,
    sections: (name) => [
      { title: `Notarial Services in ${name}`, content: `Notarization is required for many legal documents in ${name}, including deeds of sale, affidavits, and Special Power of Attorney. LegaSecure's Online SPA reduces the need for in-person notarization.` },
      { title: 'Common Documents Needing Notarization', items: ['Special Power of Attorney (SPA)', 'Deed of Absolute Sale', 'Affidavit of Loss', 'Extra-Judicial Settlement', 'Contracts and Agreements', 'Sworn Statements'] },
    ],
    faqsFn: (name) => [
      { question: `Where can I find a notary in ${name}?`, answer: `Notaries in ${name} are typically lawyers commissioned by the court. You can find them at law offices, malls, and government offices. For SPA, consider LegaSecure's online option.` },
      { question: `How much does notarization cost in ${name}?`, answer: `Notarization fees in ${name} vary from P100-500 for simple documents to P1,000-5,000+ for complex documents like deeds of sale.` },
      { question: `Can I notarize documents online?`, answer: `While traditional notarization requires physical presence, LegaSecure's Online SPA feature generates Apostille-ready documents digitally.` },
    ],
  },
  'inheritance': {
    titleFn: (name) => `Inheritance & Succession Law in ${name}`,
    metaFn: (name, prov) => `Understanding inheritance and succession law in ${name}, ${prov}. Estate tax, compulsory heirs, property transfer.`,
    heroFn: (name) => `When a loved one passes, the last thing your family in ${name} should worry about is legal paperwork. Plan ahead so they don't have to guess — LegaSecure makes inheritance simple.`,
    sections: (name) => [
      { title: `Inheritance Law in ${name}`, content: `Philippine inheritance law applies to all properties in ${name}. Understanding compulsory heirs, legitime portions, and estate tax obligations is essential for proper succession planning.` },
      { title: 'Key Inheritance Concepts', items: ['Compulsory Heirs (children, spouse, parents)', 'Legitime — the portion that must go to compulsory heirs', 'Free Portion — what you can freely distribute', 'Estate Tax — 6% on net estate above P5 million', 'Extra-Judicial Settlement — when all heirs agree', 'Intestate Succession — when there is no will'] },
    ],
    faqsFn: (name) => [
      { question: `How is inheritance handled in ${name}?`, answer: `Inheritance in ${name} follows Philippine law. If there's a will, it's executed accordingly. Without a will, intestate succession rules apply automatically.` },
      { question: `What is the estate tax for property in ${name}?`, answer: `The Philippine estate tax is a flat 6% on the net estate exceeding P5 million. This applies to all properties including those in ${name}.` },
      { question: `How do I settle an estate in ${name} from abroad?`, answer: `You can participate in estate settlement via SPA. LegaSecure's Online SPA lets you authorize a representative to handle the process in ${name}.` },
    ],
  },
};

export default function CityServicePage() {
  const { slug } = useParams();
  const [city, setCity] = useState(null);
  const [loading, setLoading] = useState(true);

  const path = window.location.pathname;
  const serviceType = path.startsWith('/estate-planning/') ? 'estate-planning' : path.startsWith('/property/') ? 'property' : path.startsWith('/notary/') ? 'notary' : 'inheritance';
  const tpl = TEMPLATES[serviceType];

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/cities/${slug}`).then(r => {
      setCity(r.data);
      setCanonical(`/${serviceType}/${slug}`);
      setMeta(`${tpl.titleFn(r.data.name)} | LegaSecure Guide`, tpl.metaFn(r.data.name, r.data.province));
    }).catch(() => setCity(null)).finally(() => setLoading(false));
  }, [slug, serviceType, tpl]);

  if (loading) return <div className="ls-container py-20 text-center"><i className="fa-solid fa-spinner fa-spin text-2xl text-[var(--ls-navy)]"></i></div>;
  if (!city) return <div className="ls-container py-20 text-center"><h1 className="text-2xl font-bold">City not found</h1></div>;

  const faqs = tpl.faqsFn(city.name);
  const sections = tpl.sections(city.name);

  return (
    <div data-testid={`${serviceType}-city-page`}>
      <FAQSchema faqs={faqs} />
      <BreadcrumbSchema items={[{ name: 'Home', url: '/' }, { name: 'Cities', url: '/all-cities' }, { name: city.name, url: `/city/${slug}` }, { name: tpl.titleFn(city.name).replace(` in ${city.name}`, '') }]} />
      <SpeakableSchema selectors={["h1", ".speakable-intro"]} />
      <ServiceSchema />

      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://images.unsplash.com/photo-1560520653-9e0e4c89eb11?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&w=1920&q=80" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-white/80"></div>
        </div>
        <div className="ls-container relative z-10 py-12">
          <nav className="text-sm text-[var(--ls-text-light)] mb-4">
            <Link to="/" className="hover:text-[var(--ls-gold)]">Home</Link> / <Link to="/all-cities" className="hover:text-[var(--ls-gold)]">Cities</Link> / <Link to={`/city/${slug}`} className="hover:text-[var(--ls-gold)]">{city.name}</Link> / <span className="text-[var(--ls-navy)] font-medium">{serviceType === 'estate-planning' ? 'Estate Planning' : serviceType === 'property' ? 'Property' : serviceType === 'notary' ? 'Notary' : 'Inheritance'}</span>
          </nav>
          <h1 className="text-3xl md:text-4xl font-bold font-['Crimson_Pro'] mb-3" style={{ color: '#1B365D' }}>{tpl.titleFn(city.name)}</h1>
          <p className="speakable-intro text-lg max-w-2xl" style={{ color: '#4A5568' }}>{tpl.heroFn(city.name)}</p>
          <a href="https://legasecure.com" className="ls-btn-navy mt-6 inline-block">Get Started Free</a>
        </div>
      </section>

      <section className="ls-section">
        <div className="ls-container max-w-4xl">
          {sections.map((sec, i) => (
            <div key={i} className="ls-card prose-content mb-6">
              <h2>{sec.title}</h2>
              {sec.content && <p>{sec.content}</p>}
              {sec.items && <ul>{sec.items.map((item, j) => <li key={j} className="ml-6 list-disc mb-1">{item}</li>)}</ul>}
            </div>
          ))}

          <div className="mb-12">
            <h2 className="text-2xl font-bold mb-6">Frequently Asked Questions</h2>
            <div className="space-y-3">
              {faqs.map((f, i) => <FAQItem key={i} question={f.question} answer={f.answer} />)}
            </div>
          </div>

          {city.nearby_cities?.length > 0 && (
            <div>
              <h2 className="text-2xl font-bold mb-4">Nearby Cities</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {city.nearby_cities.map(c => (
                  <Link key={c.slug} to={`/${serviceType}/${c.slug}`} className="ls-card p-4 flex items-center gap-3">
                    <i className="fa-solid fa-city text-[var(--ls-gold)]"></i>
                    <div><p className="font-semibold text-sm">{c.name}</p><p className="text-xs text-[var(--ls-text-light)]">{c.province}</p></div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      <section className="bg-[var(--ls-bg)] py-12">
        <div className="ls-container text-center">
          <h2 className="text-2xl font-bold text-white mb-4">Start Protecting Your Assets in {city.name}</h2>
          <a href="https://legasecure.com" className="ls-btn-gold text-lg px-8 py-3">Get Started <i className="fa-solid fa-arrow-right ml-2"></i></a>
        </div>
      </section>
    </div>
  );
}

function FAQItem({ question, answer }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="ls-card cursor-pointer" onClick={() => setOpen(!open)}>
      <div className="flex justify-between items-center">
        <h3 className="font-semibold text-sm text-[var(--ls-navy)] pr-4">{question}</h3>
        <i className={`fa-solid fa-chevron-down text-[var(--ls-text-light)] transition-transform text-xs ${open ? 'rotate-180' : ''}`}></i>
      </div>
      {open && <p className="mt-2 text-sm text-[var(--ls-text-light)] leading-relaxed">{answer}</p>}
    </div>
  );
}
